import {
  Autocomplete,
  Box,
  CircularProgress,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  DisabledJobRatings,
  JobOrder,
  jobOrderSearchParams,
  JobRating,
  JobRatingLabel,
  Publish,
  PublishLabel,
} from "../../common/JobOrder";
import { DisabledPorts, PortMasterLabel } from "../../common/Port";
import "./JobBoardListComponent.scss";
import { Vessel } from "../../common/Vessel";
import { useGetJobOrdersQuery } from "../../redux/api/jobOrder";
import { useGetPortsQuery, useGetVesselsQuery } from "../../redux/api/vessel";
import { jobRatingStringToEnum } from "../../util/jobRating.util";
import useDocumentTitle from "../../util/useDocumentTitle";
import { Email } from "@mui/icons-material";
import { formatDateString } from "../../common/FormatDateString";
import { useSelector } from "react-redux";
import {
  selectCurrentProfile,
  selectCurrentUser,
} from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { MarinerProfile, MarinerType } from "../../common/MarinerProfile";
import { useGetJobApplicationsQuery } from "../../redux/api/jobApplication";

type jobPosting = {
  id: number;
  date: number;
  vessel: string;
  rating: string;
  port: string;
  offsigner: string;
  onsigner: string;
  completed: number;
  notApplicable: number;
  tasks: number;
  publish: string;
};

const JobBoardListComponent = () => {
  useDocumentTitle("Job Board");
  const currentProfile: MarinerProfile = useSelector(selectCurrentProfile);
  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES;
  const compatibleWithRating =
    (user?.userGroup as UserGroup) === UserGroup.MARINERS
      ? currentProfile?.rating
      : undefined;
  const { data: portsFromApi } = useGetPortsQuery();
  const [jobOrderSearchParams, setJobOrderSearchParams] =
    useState<jobOrderSearchParams>({
      assigneeId: "",
      vesselId: "",
      port: "",
      rating: undefined,
      compatibleWithRating: compatibleWithRating,
      jobOrderID: "",
      fillByAfterDate: new Date().toISOString(),
      fillByBeforeDate: undefined,
      completed: false,
      notApplicable: false,
      completedOrNotApplicable: undefined,
      deleted: false,
      archived: false,
      critical: undefined,
      newJobOrder: undefined,
      modifiedByCaptain: undefined,
      publishStatuses: [Publish.OPEN, Publish.FILLING],
      smis: undefined,
      profileId: undefined,
      page: 0,
      size: 15,
      sortColumn: "fillByDate",
      sortDirection: "asc",
    });
  const [searchParams] = useSearchParams();
  const [ports, setPorts] = useState<string[]>([]);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [port, setPort] = useState<string | null>(null);
  const [rating, setRating] = useState<JobRating | null>(null);
  // variable to skip the initial GET query until the query params have been processed
  const [skipQuery, setSkipQuery] = useState(
    Array.from(searchParams).length > 0
  );
  // variable to track whether the query params have been processed
  const [searchParamsProcessed, setSearchParamsProcessed] = useState(false);

  const { data: vessels, isLoading: isVesselsLoading } = useGetVesselsQuery({});

  const {
    data: jobOrdersFromApi,
    isLoading: isJobOrdersLoading,
    refetch,
  } = useGetJobOrdersQuery(jobOrderSearchParams, { skip: skipQuery });

  let jobOrders: jobPosting[] = (jobOrdersFromApi?.content as JobOrder[])?.map(
    (job: any) => {
      return {
        id: job.id,
        date: job.fillByDate,
        vessel: job.vessel?.name,
        rating: job.rating,
        port: job.port,
        offsigner: !!job.offsigner
          ? job.offsigner?.firstName + " " + job.offsigner?.lastName
          : "",
        onsigner: !!job.onsigner
          ? job.onsigner?.firstName + " " + job.onsigner?.lastName
          : "",
        completed: job.checklistRoot?.subTasksCompletedCount | 0,
        notApplicable: job.checklistRoot?.subTasksNotApplicableCount | 0,
        tasks: job.checklistRoot?.subTasksCount | 0,
        publish: job.publish,
      };
    }
  );

  const { data: jobApplicationsFromApi, isLoading: isJobApplicationsLoading } =
    useGetJobApplicationsQuery(
      { appliedById: currentProfile?.id, jobOrderId: undefined },
      {
        skip:
          !currentProfile ||
          !currentProfile?.id ||
          currentProfile?.type === MarinerType.UNLICENSED,
      }
    );

  useEffect(() => {
    (async () => {
      searchParams.forEach((s, k) => {
        if (!s) return;
        switch (k) {
          case "vesselId":
            if (!Number(s)) break;
            const currVessel = vessels?.find((v) => v.id === Number(s));
            setVessel(currVessel ? currVessel : null);
            break;
          case "rating":
            const currRating = jobRatingStringToEnum(s);
            setRating(currRating ? currRating : null);
            break;
          default:
            // do nothing
            break;
        }
      });
      setSearchParamsProcessed(true);
    })();
  }, [searchParams, vessels]);

  useEffect(() => {
    if (skipQuery && searchParamsProcessed) {
      setSkipQuery(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOrderSearchParams]);

  useEffect(() => {
    let portOptions;
    if (!!vessel?.ports) {
      portOptions = vessel.ports;
    } else {
      portOptions = portsFromApi;
      //filter out disabled ports
      portOptions = portOptions?.filter(
        (port) => !DisabledPorts.some((dis) => PortMasterLabel[dis] === port)
      );
    }
    if (!!portOptions) {
      setPorts(portOptions);
    }
  }, [vessel?.ports, portsFromApi]);

  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      vesselId: vessel?.id ? `${vessel.id}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vessel]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      rating: rating ? rating : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      port: port ? port : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port]);

  const handleSort =
    (property: keyof JobOrder) => (event: React.MouseEvent<unknown>) => {
      const direction =
        jobOrderSearchParams.sortColumn === property &&
        jobOrderSearchParams.sortDirection === "asc"
          ? "desc"
          : "asc";
      setJobOrderSearchParams({
        ...jobOrderSearchParams,
        sortColumn: property,
        sortDirection: direction,
      });

      refetch();
    };

  if (isJobOrdersLoading || isVesselsLoading || isJobApplicationsLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <h1>Job Board </h1>
      <div className="filters-wrap">
        <div className="filters-list">
          <label>
            <span>Job Order ID:</span>
            <OutlinedInput
              value={jobOrderSearchParams.jobOrderID || ""}
              onChange={(e) =>
                setJobOrderSearchParams({
                  ...jobOrderSearchParams,
                  jobOrderID: e.target.value,
                })
              }
              type="text"
            />
          </label>
          <label>
            <span>Rating:</span>
            <Autocomplete
              id="Rating"
              options={Object.keys(JobRating).filter(
                (rating) =>
                  !DisabledJobRatings.has(JobRating[rating as JobRating])
              )}
              getOptionLabel={(option) => JobRatingLabel[option as JobRating]}
              value={rating}
              onChange={(event, value: any) => {
                setRating(JobRating[value as JobRating]);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </label>
          <label>
            <span>Port</span>
            <Autocomplete
              id="port"
              options={ports ? ports : []}
              value={port}
              onChange={(event, value) => setPort(value)}
              renderInput={(params) => <TextField {...params} />}
              getOptionLabel={(opt) => (opt ? opt : "")}
            />
          </label>

          <label>
            <span>Crew Change Needed</span>
            <OutlinedInput
              fullWidth
              size="small"
              type="date"
              value={
                jobOrderSearchParams.fillByBeforeDate?.substring(0, 10) || ""
              }
              onChange={(e) => {
                const date = !!e.target.value
                  ? new Date(e.target.value).toISOString()
                  : undefined;
                setJobOrderSearchParams({
                  ...jobOrderSearchParams,
                  fillByAfterDate: date,
                  fillByBeforeDate: date,
                });
              }}
            />
          </label>
          <label>
            <span>Vessel:</span>
            <Autocomplete
              id="Vessel"
              options={
                vessels ? (vessels as Vessel[]).filter((v) => !v.disabled) : []
              }
              value={vessel}
              onChange={(event, value) => setVessel(value)}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => (opt.name ? opt.name : "")}
            />
          </label>
        </div>
      </div>

      <TableContainer>
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell className="wd-140px" align="center">
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "id"}
                  direction={
                    jobOrderSearchParams.sortColumn === "id"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("id")}
                >
                  Job Order ID
                </TableSortLabel>
              </TableCell>
              <TableCell className="wd-140px">
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "rating"}
                  direction={
                    jobOrderSearchParams.sortColumn === "rating"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("rating")}
                >
                  Rating
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "port"}
                  direction={
                    jobOrderSearchParams.sortColumn === "port"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("port")}
                >
                  Port
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "fillByDate"}
                  direction={
                    jobOrderSearchParams.sortColumn === "fillByDate"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("fillByDate")}
                >
                  Crew Change Needed
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "vessel"}
                  direction={
                    jobOrderSearchParams.sortColumn === "vessel"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("vessel")}
                >
                  Vessel
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "publish"}
                  direction={
                    jobOrderSearchParams.sortColumn === "publish"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("publish")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {/*Apply column has no header */}
              {!isViewOnly && <TableCell></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {jobOrders?.map((row, index) => {
              return (
                <TableRow key={"jobOrder" + index}>
                  <TableCell>
                    <Link className="blue-link" to={`/jobBoard/${row.id}`}>
                      {row.id}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={JobRatingLabel[row.rating as JobRating]}
                      placement="right"
                    >
                      <span>{row.rating.substring(1)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.port}</TableCell>
                  <TableCell>{formatDateString(row.date.toString())}</TableCell>
                  <TableCell>{row.vessel}</TableCell>
                  <TableCell>{PublishLabel[row.publish as Publish]}</TableCell>
                  {!isViewOnly && (
                    <TableCell>
                      {" "}
                      <Link
                        to={`/jobBoard/${row.id}`}
                        className={
                          !!jobApplicationsFromApi &&
                          jobApplicationsFromApi.some(
                            (jobApplication) =>
                              jobApplication.jobOrderId === row.id
                          )
                            ? "disable-grey-link"
                            : "blue-link"
                        }
                      >
                        <Email />
                        {!!jobApplicationsFromApi &&
                        jobApplicationsFromApi.some(
                          (jobApplication) =>
                            jobApplication.jobOrderId === row.id
                        )
                          ? "Applied"
                          : "Apply"}
                      </Link>{" "}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={"div"}
        count={(jobOrdersFromApi?.totalElements as number) || 0}
        onPageChange={(event, newPage) =>
          setJobOrderSearchParams({
            ...jobOrderSearchParams,
            page: newPage,
          })
        }
        rowsPerPageOptions={[15, 25, 50, 100]}
        page={jobOrderSearchParams?.page}
        rowsPerPage={jobOrderSearchParams?.size}
        onRowsPerPageChange={(event) =>
          setJobOrderSearchParams({
            ...jobOrderSearchParams,
            size: parseInt(event.target.value),
            page: 0,
          })
        }
      />
    </>
  );
};

export default JobBoardListComponent;
